import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import Image1 from "../asset/WebLogos/logo20.png";

const navLinks = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Portfolio", to: "/portfolio" },
  { label: "Service", to: "/service" },
  { label: "Contact Us", to: "/contact" },
];

function Navbar({ openForm }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isContactPage = location.pathname === "/contact";

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-gray-300/30 shadow-lg"
          : "bg-white/80 backdrop-blur-xl"
      }`}
    >
      <nav className="w-full px-5 sm:px-6 lg:px-8">
     <div className="grid grid-cols-[auto_1fr_auto] lg:grid-cols-[1fr_auto_1fr] items-center h-20">
          <Link to="/" className="flex items-center shrink-0 justify-self-start">
            <img
              src={Image1}
              alt="KeyMech"
              className="h-8 lg:h-9 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation - always centered */}
          <div
            className={`hidden lg:flex py-2 px-2 rounded-full transition-all duration-500 justify-self-center ${
              scrolled
                ? "bg-white/40 backdrop-blur-lg border border-white/40 shadow-md"
                : "bg-white/95 backdrop-blur-sm border border-white/20"
            }`}
          >
            <ul className="flex items-center gap-1">
              {navLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.to}
                    className="px-5 py-2 rounded-full text-sm font-semibold text-gray-700 hover:bg-purple-100 hover:text-[#7C3AED] transition-all duration-300"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Right side: Desktop Get In Touch Button (hidden on Contact page) + Mobile Toggle */}
          <div className="flex items-center justify-self-end gap-2">
            {!isContactPage && (
              <button
                onClick={openForm}
                className="hidden lg:flex items-center justify-center font-semibold text-sm px-6 py-2.5 rounded-full transition-all duration-300 text-white shadow-md hover:shadow-lg hover:scale-105 bg-gradient-to-r from-purple-500 to-pink-500"
              >
                Get in Touch
              </button>
            )}

            {/* Mobile Toggle */}
            <button
              className="lg:hidden flex items-center justify-center p-2 text-gray-800 hover:text-purple-600 transition-colors duration-300"
              onClick={() => setOpen(!open)}
              aria-label="Toggle menu"
              aria-expanded={open}
            >
              {open ? (
                <X size={28} strokeWidth={2} />
              ) : (
                <Menu size={28} strokeWidth={2} />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: -15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              transition={{ duration: 0.25 }}
              className="lg:hidden mt-3 mb-3 bg-white/95 backdrop-blur-2xl border border-gray-200 rounded-2xl shadow-2xl overflow-hidden"
            >
              <ul className="flex flex-col p-4">
                {navLinks.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.to}
                      onClick={() => setOpen(false)}
                      className="block py-3 px-4 rounded-lg text-gray-700 font-medium hover:bg-purple-100 hover:text-[#7C3AED] transition-all duration-300"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}

                {/* Mobile Get In Touch - hidden on Contact page */}
                {!isContactPage && (
                  <button
                    onClick={() => {
                      setOpen(false);
                      openForm();
                    }}
                    className="mt-4 w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 rounded-full font-semibold hover:opacity-90 hover:shadow-lg transition-all duration-300"
                  >
                    Get in Touch
                  </button>
                )}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
}

export default Navbar;